<script lang="ts">
	import { onMount } from 'svelte'
	import { Icon, OverlayScrollbar, BaseInput } from '@repo/commons/components'
	import { clickOutside } from '@repo/commons/utils'
	import { fade, scale } from 'svelte/transition'
	import { portal } from 'svelte-portal'

	interface Props {
		/**
		 * Return current entered value.
		 */
		oninput?: ((value: string) => void) | null
		onfocus?: ((value: string) => void) | null
		onblur?: ((value: string) => void) | null
		onselect?: ((item: string) => void) | null
		onHighlightMatch?: (match: string, isExactMatch: boolean) => void | null
		/**
		 * List of items to be searched. This is irrelevant if displaySuggestions is set to false.
		 */
		items?: string[]
		/**
		 * The maximum number of items to display in the filtered results.
		 */
		topK?: number
		/**
		 * Label to display above the search input.
		 */
		label?: string
		/**
		 * Error message to display below the search input.
		 */
		error?: string
		/**
		 * Whether to display suggestions at all, below the search input.
		 */
		displaySuggestions?: boolean
		/**
		 * Placeholder text for the search input.
		 */
		placeholderText?: string
		/**
		 * Text to display when no matches are found.
		 */
		noMatchesFoundText?: string
		/**
		 * The current search query entered by the user. Only bind to this variable if you want to
		 * react to every change in the search query (i.e to filter an external list of items.)
		 * If you want to use the internal suggestion list of the component and ensure that the user
		 * can only select something that is within the list of items, use the `on:select` event instead.
		 */
		searchQuery?: string
		/**
		 * Whether to reset the search query after the user selects an item either by clicking or pressing Enter.
		 * The reset takes place each time on:select is dispatched.
		 */
		resetSearchQueryAfterSelection?: boolean
		/**
		 * The SearchIcon in the input field makes a cool animation by moving to the right and disappearing -
		 * and then moving back to the left when the input field is blurred. The limit of the movement is
		 * determined by the length of the placeholder text multiplied by this scalar. You can increase
		 * this value if the icon is not moving far enough to the right.
		 */
		widthScalar?: number
		/**
		 * Whether to fill the width of the parent container.
		 */
		fillWidth?: boolean
		/**
		 * The text pattern to highlight in the input label
		 */
		highlightPattern?: string
		inputExternalStyle?: string
	}

	let {
		items = [],
		topK = 25,
		label = '',
		error = '',
		displaySuggestions = true,
		placeholderText = '',
		noMatchesFoundText = 'No matches found',
		searchQuery = $bindable(''),
		resetSearchQueryAfterSelection = true,
		/** @deprecated */
		widthScalar = 5.5,
		/** @deprecated */
		fillWidth = false,
		inputExternalStyle = '',
		highlightPattern,
		oninput = null,
		onblur = null,
		onfocus = null,
		onselect = null,
		onHighlightMatch = () => {}
	}: Props = $props()

	if (!searchQuery) searchQuery = ''

	let filteredItems: string[] = $state([])
	let selectedIndex: number = $state(-1)
	let isOpen = $state(false)
	let inputWrapper: HTMLElement | null = $state(null)
	let dropdownPosition = $state({ top: 0, left: 0, width: 0 })

	function updateFilteredItems(): void {
		const trimmedQuery: string = searchQuery.trim()
		if (trimmedQuery === '') {
			filteredItems = []
		} else {
			filteredItems = items
				.filter((item: string) => item.toLowerCase().includes(trimmedQuery.toLowerCase()))
				.slice(0, topK)
		}
	}

	function calculateDropdownPosition() {
		if (!inputWrapper) return
		const rect = inputWrapper.getBoundingClientRect()
		const scrollY = window.scrollY || document.documentElement.scrollTop
		const scrollX = window.scrollX || document.documentElement.scrollLeft

		dropdownPosition = {
			top: rect.bottom + scrollY + 8,
			left: rect.left + scrollX,
			width: rect.width
		}
	}

	function openDropdown() {
		if (displaySuggestions && filteredItems.length > 0) {
			calculateDropdownPosition()
			isOpen = true
		}
	}

	function closeDropdown() {
		isOpen = false
		selectedIndex = -1
	}

	function handleInput(searchValue: string): void {
		searchQuery = searchValue
		selectedIndex = -1
		updateFilteredItems()

		// Simple logic: open if we have items, close if we don't
		if (filteredItems.length > 0) {
			openDropdown()
		} else {
			closeDropdown()
		}

		oninput?.(searchQuery)
	}

	function selectItem(item: string): void {
		searchQuery = item
		if (resetSearchQueryAfterSelection) searchQuery = ''
		filteredItems = []
		closeDropdown()
		onselect?.(item)
	}

	function handleItemClick(item: string): void {
		selectItem(item)
	}

	function handleKeyDown(event: KeyboardEvent): void {
		if (!isOpen || filteredItems.length === 0) return

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault()
				selectedIndex = selectedIndex < filteredItems.length - 1 ? selectedIndex + 1 : selectedIndex
				break
			case 'ArrowUp':
				event.preventDefault()
				selectedIndex = selectedIndex > 0 ? selectedIndex - 1 : selectedIndex
				break
			case 'Enter':
				event.preventDefault()
				if (selectedIndex >= 0 && selectedIndex < filteredItems.length) {
					selectItem(filteredItems[selectedIndex])
				}
				break
			case 'Escape':
				event.preventDefault()
				closeDropdown()
				break
		}
	}

	function handleInputFocus(): void {
		updateFilteredItems()
		if (filteredItems.length > 0) {
			openDropdown()
		}
		onfocus?.(searchQuery)
	}

	function handleClickOutside(event: Event) {
		if (inputWrapper && inputWrapper.contains(event.target as Node)) {
			return
		}
		closeDropdown()
		onblur?.(searchQuery)
	}

	onMount(() => {
		updateFilteredItems()
		window.addEventListener('keydown', handleKeyDown)
		return () => window?.removeEventListener('keydown', handleKeyDown)
	})
</script>

<div class="search-container">
	<div class="input-wrapper" style={inputExternalStyle} bind:this={inputWrapper}>
		<BaseInput
			{label}
			size="md"
			placeholder={placeholderText}
			errorMessage={error}
			showError={!!error}
			clearable
			{highlightPattern}
			{onHighlightMatch}
			value={searchQuery}
			onInput={handleInput}
			onFocus={handleInputFocus}
			prepend={{
				component: Icon,
				props: {
					name: 'search',
					class: 'text-[#002C4D80]'
				}
			}}
		/>
	</div>

	{#if isOpen && filteredItems.length > 0}
		<div
			use:portal
			use:clickOutside={handleClickOutside}
			class="dropdown-card"
			style="position: fixed; top: {dropdownPosition.top}px; left: {dropdownPosition.left}px; width: {dropdownPosition.width}px; z-index: 9999;"
			in:scale={{ duration: 250, start: 0.95 }}
			out:fade={{ duration: 100 }}
		>
			<OverlayScrollbar class="search-list">
				<ul class="search-result">
					{#each filteredItems as item, index (item)}
						<li
							class:selected={selectedIndex === index}
							class="result-item"
							onclick={() => handleItemClick(item)}
							onmouseover={() => (selectedIndex = index)}
						>
							{item}
						</li>
					{/each}
					{#if filteredItems.length === 0 && searchQuery.trim()}
						<li class="no-matches">{noMatchesFoundText}</li>
					{/if}
				</ul>
			</OverlayScrollbar>
		</div>
	{/if}
</div>

<style>
	.search-container {
		@apply relative w-full;
	}

	.input-wrapper {
		@apply relative;
	}

	.dropdown-card {
		@apply min-w-[310px] py-2 bg-white rounded-lg shadow-[0px_20px_24px_-4px_rgba(0,0,0,0.08)] shadow-[0px_8px_8px_-4px_rgba(0,0,0,0.04)] border border-[#003a66]/10;
	}

	.search-result {
		@apply list-none p-0 m-0 w-full;
	}

	.result-item {
		@apply px-4 py-2 cursor-pointer text-black hover:bg-[#007ACC1A] transition-colors truncate;
	}

	.result-item.selected {
		@apply bg-[#007ACC1A];
	}

	.no-matches {
		@apply px-4 py-2 text-gray-500 cursor-not-allowed;
	}

	:global(.search-list.overlay-scrollbar) {
		@apply max-h-52 px-0;
	}
</style>
